/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';

import type { SearchResultItem } from '../../api/search/search.types';

interface UseCardInteractionArgs {
    item: SearchResultItem;
    onHover?: (item: SearchResultItem) => void;
    onHoverLeave?: (item: SearchResultItem) => void;
    shouldFocusOnMount?: boolean;
}

export function useCardInteraction({
    item,
    onHover,
    onHoverLeave,
    shouldFocusOnMount,
}: UseCardInteractionArgs) {
    const cardRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        // Focus the card on mount when it is used as a popup (e.g. with a close button).
        if (shouldFocusOnMount && cardRef.current) {
            const timeoutId = window.setTimeout(() => {
                cardRef.current?.focus();
            }, 200);

            return () => window.clearTimeout(timeoutId);
        }

        return undefined;
    }, [shouldFocusOnMount]);

    const onHoverOrFocus = () => {
        if (onHover) onHover(item);
    };

    const onHoverLeaveOrBlur = () => {
        if (onHoverLeave) onHoverLeave(item);
    };

    return { cardRef, onHoverOrFocus, onHoverLeaveOrBlur };
}
